import React, { useEffect, useState, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { ThemeProvider } from "@material-ui/core/styles";
import { createTheme } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import { colors } from "../../components/theme";
import { useNavigate, useParams } from "react-router-dom";
import {
  getTemplateInfoById,
  publishFormV2,
} from "../../services/templateV2Service";
import { parseFields } from "../TemplateBuilder/utils/parseFields";
import DocumentEditorRecipients from "./components/DocumentEditorRecipients/DocumentEditorRecipients";
import Loading from "../../components/loading";
import { toast, ToastContainer } from "react-toastify";
import recipientsService from "../../services/recipientsService";
import FieldRecipientAssignment from "./components/FieldRecipientAssignment/FieldRecipientAssignment";

// Define component props
interface TemplateBuilderRecipientsProps {}

const theme = createTheme({
  palette: {
    primary: {
      main: colors.blue,
    },
    secondary: {
      main: "#dc004e",
    },
  },
  typography: {
    fontFamily: ["Roboto", "Helvetica", "sans-serif"].join(","),
  },
});

const useStyles = makeStyles((theme) => ({
  templateBuilderContainer: {
    marginTop: 0,
    display: "flex",
    justifyContent: "space-between",
    gap: 20,
    width: "100%",
    height: "88vh", // Full viewport height for the main container
    overflow: "hidden", // Prevent scrolling on the main container
  },
  pdfViewerContainer: {
    width: "60%",
    height: "88vh",
    backgroundColor: "#f0f0f0",
  },
  recipientAssignmentContainer: {
    width: "40%",
    backgroundColor: "#ffffff",
    paddingTop: 16,
    paddingLeft: 58,
    paddingRight: 58,
    borderRadius: 8,
    maxHeight: "88vh",
    overflow: "scroll",
    paddingBottom: 85,
  },
  recipientAssignmentTitle: {
    fontSize: 32,
    fontWeight: "bold",
    marginBottom: 16,
  },
  formNameInput: {
    marginBottom: 16,
    width: "100%",
  },
  divider: {
    borderTop: "2px solid #0076FF1A",
    margin: "16px 0",
  },
}));

const TemplateBuilderRecipients: React.FC<
  TemplateBuilderRecipientsProps
> = () => {
  const classes = useStyles();
  const [currentDocumentUrl, setCurrentDocumentUrl] = useState<string>("");
  const [loadingPage, setLoadingPage] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [fields, setFields] = useState<any[]>([]);
  const [templateName, setTemplateName] = useState<string>("Untitled template");
  const [formName, setFormName] = useState<string>("");
  const [recipients, setRecipients] = useState<any[]>([]);
  const [fieldAssignments, setFieldAssignments] = useState<
    Record<string, string>
  >({});
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [recipientGroupAssignments, setRecipientGroupAssignments] = useState<
    Record<string, string>
  >({});
  const [hasAvailableGroups, setHasAvailableGroups] = useState<boolean>(false);
  const [availableGroups, setAvailableGroups] = useState<any[]>([]);
  const [isSending, setIsSending] = useState<boolean>(false);

  const navigate = useNavigate();
  const { id: currentTemplateId } = useParams<{ id: string }>();

  // Load recipients from localStorage
  useEffect(() => {
    const storedRecipients = recipientsService.getRecipients();
    setRecipients(storedRecipients);
  }, []);

  // Load template information
  useEffect(() => {
    if (currentTemplateId) {
      getTemplateInfo();
    }
  }, [currentTemplateId]);

  const getTemplateInfo = async () => {
    try {
      setLoadingPage(true);
      const response = await getTemplateInfoById(currentTemplateId || "");
      console.log("response", response);
      if (response.status === 200) {
        const parsedFields = parseFields(response.data.data.new.form[0].fields);
        console.log("Parsed fields:", parsedFields);
        setFields(parsedFields);
        const name = response.data.data.doc.fileName || "Untitled template";
        setTemplateName(name);
        setFormName(name);
        if (response.data.data.pdfUrl) {
          console.log("NEW PDF URL:", response.data.data.pdfUrl);
          setCurrentDocumentUrl(response.data.data.pdfUrl);
        } else {
          console.log("OLD Thumbnail URL:", response.data.data.doc.thumbnail);
          setCurrentDocumentUrl(response.data.data.doc.thumbnail);
        }
      }
    } catch (error) {
      console.error("Error loading template info:", error);
      toast.error("Error loading template information");
    } finally {
      setLoadingPage(false);
    }
  };

  // Handle field selection for recipient assignment
  const handleFieldSelect = (fieldId: string) => {
    // Don't allow field selection if groups are available
    if (hasAvailableGroups) {
      console.log("Field selection disabled: groups are available");
      return;
    }

    console.log("Field selected:", fieldId);
    console.log(
      "Available fields:",
      fields.map((f) => ({ id: f.id, title: f.title, type: f.type }))
    );
    setSelectedFieldId(fieldId);
  };

  // Handle groups change from child component
  const handleGroupsChange = (groups: any[]) => {
    setAvailableGroups(groups);
  };

  // Enhance fields with group information based on assignments
  const fieldsWithGroups = useMemo(() => {
    if (!hasAvailableGroups || availableGroups.length === 0) {
      return fields;
    }

    return fields.map(field => {
      // Find which recipient is assigned to this field
      const assignedRecipientId = fieldAssignments[field.id];
      if (!assignedRecipientId) {
        return field;
      }

      // Find which group is assigned to that recipient
      const assignedGroupId = recipientGroupAssignments[assignedRecipientId];
      if (!assignedGroupId) {
        return field;
      }

      // Find the group object
      const assignedGroup = availableGroups.find(g => g.id === assignedGroupId);
      if (!assignedGroup) {
        return field;
      }

      // Return field with group information
      return {
        ...field,
        fieldGroup: assignedGroup.name
      };
    });
  }, [fields, fieldAssignments, recipientGroupAssignments, hasAvailableGroups, availableGroups]);

  // Handle recipient assignment to field
  const handleAssignRecipient = (fieldId: string, recipientId: string) => {
    setFieldAssignments((prev) => ({
      ...prev,
      [fieldId]: recipientId,
    }));
  };

  // Handle removing recipient assignment from field
  const handleRemoveRecipient = (fieldId: string) => {
    setFieldAssignments((prev) => {
      const newAssignments = { ...prev };
      delete newAssignments[fieldId];
      return newAssignments;
    });
  };

  // Handle recipient group assignments
  const handleRecipientGroupAssignmentChange = (assignments: Record<string, string>) => {
    console.log("Updating recipient group assignments:", assignments);
    setRecipientGroupAssignments(assignments);
  };

  // Handle final publish with field assignments
  const handlePublish = async () => {
    console.log("Publishing template...");
    console.log("fieldAssignments", fieldAssignments);
    console.log("hasAvailableGroups", hasAvailableGroups);
    console.log("availableGroups", availableGroups);
    console.log("recipientGroupAssignments", recipientGroupAssignments);
    setIsSending(true);
    try {
      let userRequest: any[] = [];

      if (hasAvailableGroups && availableGroups.length > 0) {
        // When groups are available, create user entries based on group assignments
        console.log("Groups mode: Creating userRequest based on group assignments");
        
        userRequest = Object.entries(recipientGroupAssignments).map(([recipientId, groupId], index) => {
          const recipient = recipients.find(r => r.id === recipientId);
          const assignedGroup = availableGroups.find(g => g.id === groupId);
          
          const userObj = {
            name: recipient?.name || `User ${index + 1}`,
            email: recipient?.email || `user${index + 1}@test.com`,
            order: recipient?.order || index + 1,
           // fields: [0], // [0] when groups are used
            fieldGroup: assignedGroup?.name || ""
          };
          
          console.log(`Group mode - User: ${userObj.name}, Group: ${userObj.fieldGroup}, Fields: [0]`);
          return userObj;
        });
        
      } else {
        // When no groups, use traditional field assignment logic
        console.log("Field mode: Creating userRequest based on field assignments");
        
        // Group fields by recipient (use actual fieldId from API, not array index)
        const recipientFieldGroups: Record<string, number[]> = {};

        // Process field assignments
        Object.entries(fieldAssignments).forEach(([fieldId, recipientId]) => {
          if (!recipientFieldGroups[recipientId]) {
            recipientFieldGroups[recipientId] = [];
          }

          // Find the field and use its numeric fieldId (from /json API), not array index
          const field = fields.find((f) => f.id === fieldId);
          if (field) {
            const numericFieldId = field.fieldId ?? Number(field.id);
            if (!Number.isNaN(numericFieldId)) {
              recipientFieldGroups[recipientId].push(numericFieldId);
              console.log(`Added fieldId ${numericFieldId} for field ID ${fieldId} to recipient ${recipientId}`);
            }
          } else {
            console.warn(`Field with ID ${fieldId} not found in fields array`);
          }
        });

        // Create userRequest array from field assignments
        userRequest = Object.entries(recipientFieldGroups).map(
          ([recipientId, fieldIds], index) => {
            const recipient = recipients.find((r) => r.id === recipientId);

            const userObj = {
              name: recipient?.name || `User ${index + 1}`,
              email: recipient?.email || `user${index + 1}@test.com`,
              order: recipient?.order || index + 1,
              fields: fieldIds.sort((a, b) => a - b), // Sort field IDs (actual IDs from API)
            };
            
            console.log(`Field mode - User: ${userObj.name}, Fields: [${userObj.fields.join(', ')}]`);
            return userObj;
          }
        );
      }

      const displayName = (formName && formName.trim()) || templateName || "Untitled template";
      const publishPayload = {
        formTemplateId: currentTemplateId || "",
        documentName: displayName,
        formName: displayName,
        userRequest: userRequest,
        ip_client: "string"
      };

      console.log("=== FINAL PUBLISH PAYLOAD ===");
      console.log(JSON.stringify(publishPayload, null, 2));
      console.log("=== PAYLOAD VALIDATION ===");
      console.log(`Mode: ${hasAvailableGroups && availableGroups.length > 0 ? 'GROUPS' : 'FIELDS'}`);
      console.log("- formTemplateId:", publishPayload.formTemplateId);
      console.log("- documentName:", publishPayload.documentName);
      console.log("- ip_client:", publishPayload.ip_client);
      console.log("- userRequest array length:", publishPayload.userRequest.length);
      
      publishPayload.userRequest.forEach((user, index) => {
        if (hasAvailableGroups && availableGroups.length > 0) {
          console.log(`- User ${index + 1} [GROUP MODE]:`, {
            name: user.name,
            email: user.email,
            order: user.order,
            fields: user.fields, // Should be [0]
            fieldGroup: user.fieldGroup
          });
        } else {
          console.log(`- User ${index + 1} [FIELD MODE]:`, {
            name: user.name,
            email: user.email,
            order: user.order,
            fields: user.fields // Field IDs from API (e.g. 1, 2, 3, 4)
          });
        }
      });

      const response = await publishFormV2(publishPayload);
      toast.success("Template published with field assignments!");
      setTimeout(() => {
        navigate("/templates-v2");
      }, 1500);
      console.log("Response publishFormV2Recipients:", response);
    } catch (error) {
      console.error("Error publishing template:", error);
      toast.error("Error publishing template");
    } finally {
      setIsSending(false);
    }
  };

  // Show all fields for assignment (all fields can be assigned to recipients)
  const assignableFields = fields;

  return (
    <div>
      <ThemeProvider theme={theme}>
        <ToastContainer />

        <div className={classes.templateBuilderContainer}>
          {/* PDF Viewer - Left Side */}
          <div className={classes.pdfViewerContainer}>
            {currentDocumentUrl && (
              <DocumentEditorRecipients
                fileUrl={currentDocumentUrl}
                templateName={templateName}
                fields={fieldsWithGroups}
                setFields={setFields}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                onNumPages={(n: number) => setNumPages(n)}
                fieldAssignments={fieldAssignments}
                onFieldSelect={handleFieldSelect}
                recipients={recipients}
                groups={availableGroups}
              />
            )}
          </div>

          {/* Recipient Assignment - Right Side */}
          <div className={classes.recipientAssignmentContainer}>
            <div className={classes.recipientAssignmentTitle}>Signature</div>
            <div className={classes.divider}></div>

            <TextField
              className={classes.formNameInput}
              label="Form name"
              placeholder={templateName || "Untitled template"}
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              helperText="Name shown to recipients when they receive the form"
              inputProps={{ "aria-label": "Form name" }}
            />

            <FieldRecipientAssignment
              fields={assignableFields}
              recipients={recipients}
              fieldAssignments={fieldAssignments}
              selectedFieldId={selectedFieldId}
              recipientGroupAssignments={recipientGroupAssignments}
              onAssignRecipient={handleAssignRecipient}
              onRemoveRecipient={handleRemoveRecipient}
              onRecipientGroupAssignmentChange={handleRecipientGroupAssignmentChange}
              onClearSelection={() => setSelectedFieldId(null)}
              onFieldSelect={handleFieldSelect}
              onPublish={handlePublish}
              onGroupsAvailableChange={setHasAvailableGroups}
              onGroupsChange={handleGroupsChange}
              isSending={isSending}
            />
          </div>
        </div>

        <Loading show={loadingPage} text={"Loading template..."} />
      </ThemeProvider>
    </div>
  );
};

export default TemplateBuilderRecipients;
